import {
  PDObject,
  PDSource,
  ProcessDesignerAction,
} from '@flowx/designer/modules/analytics/analytics-process-designer.entity'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import {
  NodeAction,
  NodeActionDisplayIn,
} from '@flowx/designer/modules/bpmn-editor/bpmn-graph.constants'
import { BpmnNodeRxjsEvents } from '@flowx/designer/modules/bpmn-editor/bpmn-node-rxjs.events'
import { BpmnSwimlaneRxjsEvents } from '@flowx/designer/modules/bpmn-editor/bpmn-swimlane-rxjs.events'
import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { AreasEvents } from '@flowx/designer/shared/effector/area/area.events'
import { BoundaryEventType } from '@flowx/designer/shared/entities/boundary-event/boundary-event.common'
import { BoundaryEvent } from '@flowx/designer/shared/entities/boundary-event/boundary-event.types'
import { NavigationType } from '@flowx/designer/shared/entities/nav-area.entity'
import {
  ActivityNode,
  EventNodes,
  GatewayNodes,
  NodeType,
} from '@flowx/designer/shared/entities/node.types'
import { SequencePoints } from '@flowx/designer/shared/entities/sequence.dictionary'

import { BPMNNodeActionsEvents } from './components/bpmn-editor-node-actions/store/bpmn-node-actions.events'

// BPMN Nodes 2.0 Actions
export function getNodeActionsByType(
  node: NodeDefinition | BoundaryEvent,
  allowReadOnlyInProcessConfiguration: boolean,
  analyticsService?: AnalyticsService
): NodeAction[] {
  const { type } = node
  let allowReadOnly = 'allowReadOnly' in node ? node.allowReadOnly : false

  const nodeUI: NodeAction = {
    icon: 'actions-ui',
    label: '',
    displayInReadonly: true,
    displayIn: NodeActionDisplayIn.All,
    tooltip: 'UI Designer',
    command: () => {
      analyticsService?.trackProcessDesigner({
        event: { object: PDObject.PROCESS_DESIGNER, action: ProcessDesignerAction.UI_DESIGNER },
        properties: { source: PDSource.NODE },
      })
      BpmnEditorEvents.nodeActionChangeViewToUI()
    },
  }
  const nodeConfig: NodeAction = {
    icon: 'actions-config',
    label: '',
    displayInReadonly: true,
    displayIn: NodeActionDisplayIn.All,
    tooltip: 'Node settings',
    command: () => {
      analyticsService?.trackProcessDesigner({
        event: { object: PDObject.PROCESS_DESIGNER, action: ProcessDesignerAction.SETTINGS },
        properties: { source: PDSource.NODE },
      })
      BpmnEditorEvents.nodeActionOpenConfigPanel()
    },
  }
  const readonlyToggle: NodeAction = {
    icon: !allowReadOnly ? 'eye-slash' : 'eye',
    label: '',
    displayInReadonly: false,
    displayIn: NodeActionDisplayIn.All,
    tooltip: 'Toggle Node Visibility in Read-Only',
    command: () => {
      if ('allowReadOnly' in node) {
        allowReadOnly = !allowReadOnly
        const icon = !allowReadOnly ? 'eye-slash' : 'eye'
        readonlyToggle.icon = icon
        BpmnEditorEvents.nodeActionToggleReadonly(allowReadOnly)
      }
    },
  }
  const nodeCopy: NodeAction = {
    icon: 'actions-copy',
    label: '',
    displayInReadonly: true,
    displayIn: NodeActionDisplayIn.All,
    tooltip: 'Copy Node',
    command: () => BpmnNodeRxjsEvents.nodeCtxMenuCopy.next(),
  }
  const nodeDelete: NodeAction = {
    icon: 'actions-delete',
    label: '',
    tooltip: 'Delete Node',
    command: () => BpmnEditorEvents.nodeCtxMenuDeleteNode(),
  }
  // TODO: rewire to the AI builder harness
  // const nodeAi: NodeAction = {
  //   icon: 'stars-flowx-yellow',
  //   label: '',
  //   tooltip: 'AI Command Center',
  //   command: () => BpmnEditorEvents.nodeAiSubPanel(),
  // }
  //
  // const aiActions = getNodeAiActions(type, designerAiAccess).length ? [nodeAi] : []
  const aiActions: NodeAction[] = []

  switch (type) {
    case ActivityNode.USER_TASK: {
      return [
        nodeUI,
        nodeConfig,
        ...(allowReadOnlyInProcessConfiguration ? [readonlyToggle] : []),
        nodeCopy,
        nodeDelete,
        ...aiActions,
      ]
    }
    case ActivityNode.TASK:
    case ActivityNode.MESSAGE_EVENT_SEND:
    case ActivityNode.MESSAGE_EVENT_RECEIVE:
    case GatewayNodes.GATEWAY_EXCLUSIVE:
    case GatewayNodes.GATEWAY_PARALLEL:
    case EventNodes.START_EVENT:
    case EventNodes.END_EVENT: {
      return [nodeConfig, nodeCopy, nodeDelete, ...aiActions]
    }
    case EventNodes.INTERMEDIATE_MESSAGE_THROW_EVENT:
    case EventNodes.INTERMEDIATE_MESSAGE_CATCH_EVENT:
    case EventNodes.START_MESSAGE_CATCH_EVENT:
    case EventNodes.START_TIMER_EVENT:
    case EventNodes.INTERMEDIATE_TIMER_EVENT: {
      return [nodeConfig, nodeCopy, nodeDelete]
    }
    case BoundaryEventType.MESSAGE_THROW:
    case BoundaryEventType.MESSAGE_CATCH_INTERRUPTING:
    case BoundaryEventType.MESSAGE_CATCH_NON_INTERRUPTING:
    case BoundaryEventType.TIMER_INTERRUPTING:
    case BoundaryEventType.ERROR_CATCH_INTERRUPTING:
    case BoundaryEventType.TIMER_NON_INTERRUPTING: {
      return [nodeConfig, nodeDelete]
    }
    case ActivityNode.SUBPROCESS_RUN: {
      return [
        nodeConfig,
        ...(allowReadOnlyInProcessConfiguration ? [readonlyToggle] : []),
        nodeCopy,
        nodeDelete,
        ...aiActions,
      ]
    }
    case ActivityNode.SUBPROCESS_EMBED: {
      return [
        nodeUI,
        nodeConfig,
        ...(allowReadOnlyInProcessConfiguration ? [readonlyToggle] : []),
        nodeCopy,
        nodeDelete,
        ...aiActions,
      ]
    }
    default: {
      return []
    }
  }
}

export function getFloatingActions(): NodeAction[] {
  const nodePaste: NodeAction = {
    icon: 'actions-paste',
    label: 'Paste',
    command: () => BpmnSwimlaneRxjsEvents.contextMenuPaste.next(),
  }

  return [nodePaste]
}

export function getEdgeActions(sequenceId: SequencePoints['id']): NodeAction[] {
  // The id is captured at menu-open time: selectedSequenceId$ can reset (canvas
  // click, selection cleared, version change) while the menu stays visible, which
  // made a later Delete click a silent no-op.
  const edgeDelete: NodeAction = {
    icon: 'actions-delete',
    label: 'Delete',
    command: () => BpmnEditorEvents.edgeCtxMenuDelete(sequenceId),
  }

  // TODO: This is temporarly disabled until we solve all the critical bugs
  // TODO: rewire to the AI builder harness
  // const sequenceAi: NodeAction = {
  //   icon: 'stars-flowx-yellow',
  //   label: '',
  //   command: ({ store }) => {
  //     store.dispatch(SequenceMenuActions.generateProcess({ sequenceId }))
  //   },
  // }

  return [edgeDelete]
}

export function getMultiselectActions(): NodeAction[] {
  const nodesCopy: NodeAction = {
    icon: 'actions-copy',
    label: 'Copy',
    displayInReadonly: true,
    command: () => BpmnNodeRxjsEvents.multiselectCtxMenuCopy.next(),
  }
  const nodesDelete: NodeAction = {
    icon: 'actions-delete',
    label: 'Delete',
    command: () => BpmnEditorEvents.multiselectCtxMenuDeleteNodes(),
  }

  return [nodesCopy, nodesDelete]
}

export function getAreaActions(area: any): NodeAction[] {
  const nodeUI: NodeAction = {
    icon: 'actions-ui',
    label: '',
    displayInReadonly: true,
    displayIn: NodeActionDisplayIn.All,
    command: () => AreasEvents.navigateToUIDesigner(area),
  }

  const deleteArea: NodeAction = {
    icon: 'actions-delete',
    label: '',
    displayInReadonly: false,
    displayIn: NodeActionDisplayIn.All,
    command: () => {
      if (area.type === NavigationType.PARENT_PROCESS) {
        AreasEvents.removeParentProcessArea(area)
      } else {
        AreasEvents.removeArea(area)
      }
    },
  }

  return [...(area.type !== NavigationType.PARENT_PROCESS ? [nodeUI] : []), deleteArea]
}

export function getInstanceActionsByNodeType(
  type: NodeType | BoundaryEventType,
  definitionsExist: boolean
): NodeAction[] {
  const navToDefinition: NodeAction = {
    icon: 'pencil',
    label: 'Edit Process Definition',
    displayInReadonly: true,
    displayIn: NodeActionDisplayIn.All,
    command: () => BPMNNodeActionsEvents.navToProcessDefinition({ openWindow: true }),
  }
  const navToSubprocessInstance: NodeAction = {
    icon: 'arrow-right',
    label: 'Open Subprocess Instance',
    displayInReadonly: true,
    displayIn: NodeActionDisplayIn.All,
    command: () => BPMNNodeActionsEvents.navToEmbeddedInstance(),
  }
  switch (type) {
    case ActivityNode.SUBPROCESS_EMBED: {
      return definitionsExist
        ? [navToDefinition, navToSubprocessInstance]
        : [navToSubprocessInstance]
    }
    default: {
      return definitionsExist ? [navToDefinition] : []
    }
  }
}

export function getNodeAiActions(
  type: NodeType | BoundaryEventType,
  designerAiAccess: boolean
): NodeAction[] {
  // TODO: product decision to cut the "Generate Process" action for now, check back in the future

  const nodeAiDeveloperAction: NodeAction = {
    icon: 'action-developer',
    label: 'Generate Business Rules',
    // TODO: rewire to the AI builder harness
    command: () => {},
  }

  const nodeAiDesignerAction: NodeAction = {
    icon: 'action-designer',
    label: 'Generate UI',
    // TODO: rewire to the AI builder harness
    command: () => {},
  }

  // TODO: product decision to cut the action for now, check back in the future
  // const aiAnalystActionNodeTypes: NodeType[] = [
  //   ActivityNode.USER_TASK,
  //   ActivityNode.TASK,
  //   ActivityNode.MESSAGE_EVENT_SEND,
  //   ActivityNode.MESSAGE_EVENT_RECEIVE,
  //   GatewayNodes.GATEWAY_EXCLUSIVE,
  //   GatewayNodes.GATEWAY_PARALLEL,
  //   EventNodes.START_EVENT,
  //   EventNodes.END_EVENT,
  //   ActivityNode.SUBPROCESS_RUN,
  // ]
  const aiDesignerActionNodeTypes: NodeType[] = [ActivityNode.USER_TASK]
  const aiDeveloperActionNodeTypes: NodeType[] = [
    ActivityNode.USER_TASK,
    ActivityNode.TASK,
    ActivityNode.MESSAGE_EVENT_SEND,
  ]

  const actions = []
  // TODO: product decision to cut the action for now, check back in the future
  // if (permissions.showAnalystAgent && aiAnalystActionNodeTypes.includes(type as NodeType)) {
  //   actions.push(nodeAiAnalystAction)
  // }
  if (designerAiAccess && aiDesignerActionNodeTypes.includes(type as NodeType)) {
    actions.push(nodeAiDesignerAction)
  }
  if (designerAiAccess && aiDeveloperActionNodeTypes.includes(type as NodeType)) {
    actions.push(nodeAiDeveloperAction)
  }

  return actions
}
